<script setup>
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import ButtonV4 from 'next/button/Button.vue';
import AccountAPI from 'dashboard/api/account';

const { t } = useI18n();

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['success', 'close']);

const isLoading = ref(false);
const isStripeLoaded = ref(false);
const stripe = ref(null);
const cardElement = ref(null);
const cardErrors = ref('');
const isFormValid = ref(false);
const stripeUnavailable = ref(false);

const stripePublishableKey = computed(() => {
  // Get from window config or environment
  return window.chatwootConfig?.stripePublishableKey || '';
});

const loadStripe = async () => {
  try {
    if (!stripePublishableKey.value) {
      stripeUnavailable.value = true;
      return;
    }

    if (window.Stripe) {
      stripe.value = window.Stripe(stripePublishableKey.value);
      isStripeLoaded.value = true;
      await nextTick();
      setupCardElement();
      return;
    }

    // Load Stripe.js if not already loaded
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = async () => {
      try {
        stripe.value = window.Stripe(stripePublishableKey.value);
        isStripeLoaded.value = true;
        await nextTick();
        setupCardElement();
      } catch (error) {
        console.warn('Unable to initialize Stripe card entry:', error);
        stripeUnavailable.value = true;
      }
    };
    script.onerror = () => {
      stripeUnavailable.value = true;
    };
    document.head.appendChild(script);
  } catch (error) {
    console.warn('Unable to load Stripe card entry:', error);
    stripeUnavailable.value = true;
  }
};

const setupCardElement = () => {
  if (!stripe.value) return;

  if (cardElement.value) {
    cardElement.value.unmount();
    cardElement.value = null;
  }

  const elements = stripe.value.elements();
  cardElement.value = elements.create('card', {
    style: {
      base: {
        fontSize: '14px',
        color: '#1f2937',
        fontFamily: 'Inter, system-ui, sans-serif',
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#ef4444',
      },
    },
  });

  cardElement.value.mount('#card-element');

  cardElement.value.on('change', event => {
    cardErrors.value = event.error ? event.error.message : '';
    isFormValid.value = event.complete;
  });
};

const handleSubmit = async () => {
  if (!stripe.value || !cardElement.value || !isFormValid.value) {
    return;
  }

  isLoading.value = true;
  cardErrors.value = '';

  try {
    // Create payment method with Stripe
    const { error, paymentMethod } = await stripe.value.createPaymentMethod({
      type: 'card',
      card: cardElement.value,
    });

    if (error) {
      cardErrors.value = error.message;
      isLoading.value = false;
      return;
    }

    // Send payment method to backend to attach to Lago customer
    await AccountAPI.addPaymentMethod(props.accountId, {
      payment_method_id: paymentMethod.id,
    });

    useAlert(t('BILLING_SETTINGS.PAYMENT_METHOD.SUCCESS'));
    emit('success');
  } catch (error) {
    console.error('Payment method error:', error);
    cardErrors.value =
      error.response?.data?.error || t('BILLING_SETTINGS.PAYMENT_METHOD.ERROR');
  } finally {
    isLoading.value = false;
  }
};

const close = () => {
  emit('close');
};

onBeforeUnmount(() => {
  if (cardElement.value) {
    cardElement.value.unmount();
    cardElement.value = null;
  }
});

onMounted(() => {
  loadStripe();
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black1 p-4 backdrop-blur-[4px]"
  >
    <div
      class="w-full max-w-lg overflow-hidden rounded-2xl border border-n-weak bg-n-surface-1 shadow-xl"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between border-b border-n-weak bg-n-solid-2 px-6 py-5"
      >
        <h3 class="text-base font-medium leading-6 text-n-slate-12">
          {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.TITLE') }}
        </h3>
        <button
          class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
          @click="close"
        >
          <i class="i-lucide-x size-4" />
        </button>
      </div>

      <!-- Body -->
      <div class="bg-n-surface-1 px-6 py-5">
        <p class="mb-5 text-sm leading-5 text-n-slate-11">
          {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.DESCRIPTION') }}
        </p>

        <!-- Stripe Card Element -->
        <div class="mb-5">
          <label class="mb-2 block text-sm font-medium text-n-slate-12">
            {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.CARD_DETAILS') }}
          </label>
          <div
            v-if="!isStripeLoaded"
            class="flex min-h-11 items-center rounded-lg border border-n-weak bg-n-input-background px-3 text-sm text-n-slate-10"
          >
            <span v-if="stripeUnavailable">
              Card entry will be available when billing is configured for this instance.
            </span>
            <span v-else>Loading secure card entry...</span>
          </div>
          <div
            v-else
            id="card-element"
            class="min-h-11 rounded-lg border border-n-weak bg-n-input-background px-3 py-3 text-n-slate-12 focus-within:border-n-brand focus-within:ring-2 focus-within:ring-n-brand/20"
          />
          <p v-if="cardErrors" class="mt-2 text-sm text-n-ruby-11">
            {{ cardErrors }}
          </p>
        </div>

        <!-- Security Notice -->
        <div
          class="flex items-start gap-3 rounded-lg border border-n-brand/20 bg-n-brand/10 p-3"
        >
          <i class="i-lucide-shield-check mt-0.5 size-4 shrink-0 text-n-brand" />
          <p class="text-xs leading-5 text-n-slate-11">
            {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.SECURITY_NOTE') }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 border-t border-n-weak bg-n-solid-2 px-6 py-4"
      >
        <ButtonV4 sm outline @click="close">
          {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.CANCEL') }}
        </ButtonV4>
        <ButtonV4
          sm
          solid
          blue
          :is-loading="isLoading"
          :disabled="!isFormValid || isLoading || !isStripeLoaded"
          @click="handleSubmit"
        >
          {{ $t('BILLING_SETTINGS.PAYMENT_METHOD.ADD_CARD') }}
        </ButtonV4>
      </div>
    </div>
  </div>
</template>
